import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react';
import type { icModel } from 'generated-ts-common';
import type { Component, FormIOSchema, FormioWebForm } from '@formio/react';
import { Form } from '@formio/react';
import { isEqual } from 'lodash';
import { Icon } from '../../basic';

import shallow from 'zustand/shallow';
import type { QuestionnaireErrorMessages } from '../../../lib/QuestionnaireStore';
import { useQuestionnaireStore } from '../../../lib/QuestionnaireStore';
import { intl, toLanguageTag } from '../../../lib';
import {
  hasConditionalQuestions,
  useFormioAnnouncements
} from '../FormioAccessibility';

type FormInstance = Omit<
  icModel.saf.infrastructure.model.form.FormInstance,
  'id'
>;
type FormInstanceContent = icModel.saf.infrastructure.model.form.FormInstanceContent;

export type QuestionnaireProps = {
  readonly value: FormInstance;
  readonly onChange: (value: FormInstance) => void;
  readonly readOnly: boolean;
  readonly onSubmission?: () => (response: boolean) => void;
  readonly previewLanguage?: string;
  readonly previewI18nMap?: Record<string, string>;
};

const currentAppLanguage: string = window.SenseConstants?.language ?? 'en';

/**
 * Compares the previous with the next properties.
 *
 * This ignores the two functions 'postOrPutFormInstance' and 'onChange'
 * @param prevProps the previous properties
 * @param nextProps the new properties
 */
function questionnaireContainerPropsAreEqual(
  prevProps: QuestionnaireProps,
  nextProps: QuestionnaireProps
): boolean {
  //we ignore the changes of the functions (postOrPutFormInstance)
  return (
    isEqual(prevProps.value, nextProps.value) &&
    prevProps.readOnly === nextProps.readOnly &&
    prevProps.onChange === nextProps.onChange &&
    prevProps.onSubmission === nextProps.onSubmission &&
    prevProps.previewLanguage === nextProps.previewLanguage &&
    isEqual(prevProps.previewI18nMap, nextProps.previewI18nMap)
  );
}

function buildFormioI18nOptions(
  selectedLanguage: string,
  previewI18nMap: Record<string, string> | undefined,
  readOnly: boolean
) {
  const normalizedLanguage = toLanguageTag(selectedLanguage);
  const baseLanguage = normalizedLanguage.split('-')[0] ?? normalizedLanguage;
  const translations = {
    ...window?.EHSLanguageMap,
    ...previewI18nMap
  };

  return {
    language: baseLanguage,
    highlightErrors: false,
    noAlerts: true,
    i18n: {
      [normalizedLanguage]: translations,
      ...(baseLanguage !== normalizedLanguage && {
        [baseLanguage]: translations
      })
    },
    readOnly
  } as const;
}

/**
 * Renders a Form.io questionnaire and keeps preview language/i18n in sync.
 */
export const QuestionnaireContainer = React.memo(function FormioContainer({
  value,
  onChange,
  readOnly,
  onSubmission,
  previewLanguage,
  previewI18nMap
}: QuestionnaireProps): React.JSX.Element {
  const conditionalHintId = useId();
  const updateFun = useRef(updateFormInstance);
  const [isFormReady, setIsFormReady] = useState(true);
  const [formioWebForm, setFormioWebForm] = useState<FormioWebForm>();
  const announcement = useFormioAnnouncements();
  const hasConditionalFields = useMemo(
    () => hasConditionalQuestions(value.schema as FormIOSchema),
    [value.schema]
  );
  const [
    errors,
    shouldShowErrors,
    setErrorWithComponentSorting,
    setAllErrors,
    resetErrors,
    setFormType,
    setPreviewTranslations,
    setPreviewLocale,
    setFormState,
    setInitialFormData
  ] = useQuestionnaireStore(
    state => [
      state.errors,
      state.shouldShowErrorAlert,
      state.setErrorWithComponentSorting,
      state.setErrors,
      state.resetErrors,
      state.setFormType,
      state.setPreviewTranslations,
      state.setPreviewLocale,
      state.setFormState,
      state.setInitialFormData
    ],
    shallow
  );

  // Sync preview translations into Zustand so Formio custom components
  // (rendered in their own createRoot() trees) can access them via useFormioI18n().
  useEffect(() => {
    setPreviewTranslations(previewI18nMap);
    return () => setPreviewTranslations(undefined);
  }, [previewI18nMap, setPreviewTranslations]);

  useEffect(() => {
    const normalized = previewLanguage
      ? toLanguageTag(previewLanguage)
      : undefined;
    setPreviewLocale(normalized);
    return () => setPreviewLocale(undefined);
  }, [previewLanguage, setPreviewLocale]);

  useEffect(() => {
    async function submitQuestionnaire(): Promise<boolean> {
      try {
        await formioWebForm?.submit();
      } catch (errorMessages: unknown) {
        if (!formioWebForm?.components) {
          return false;
        }

        setAllErrors(
          (errorMessages as QuestionnaireErrorMessages[]).reduce(
            (acc, error) => {
              acc[error.component.id] = error;
              return acc;
            },
            {} as Record<string, QuestionnaireErrorMessages>
          )
        );
        return false;
      }

      return true;
    }

    if (onSubmission) {
      submitQuestionnaire()
        .then(response => {
          onSubmission()(response);
        })
        .catch(() => {
          onSubmission()(false);
        });
    }
  }, [onSubmission, setAllErrors, formioWebForm]);

  const selectedLanguage = previewLanguage ?? currentAppLanguage;

  const options = useMemo(() => {
    return buildFormioI18nOptions(selectedLanguage, previewI18nMap, readOnly);
  }, [previewI18nMap, readOnly, selectedLanguage]);
  const onChangeWrapper = useCallback(
    (record: FormInstanceContent) => {
      if (!isFormReady) {
        return;
      } //VERY IMPORTANT otherwise you get a mismatch in the update (stale closure, ...)
      const result = updateFun.current(value, record);
      if (!result) {
        return;
      }
      onChange(result);
      if (!record?.changed?.instance) {
        return;
      }

      setFormState(record.data);

      const { error, root } = record.changed.instance;

      setErrorWithComponentSorting(
        record.changed.instance.id,
        error,
        flattenComponentKeys(root?.getComponents() ?? [])
      );
    },
    [isFormReady, onChange, setErrorWithComponentSorting, setFormState, value]
  );

  return useMemo(() => {
    const tempValue = value;
    let submission: FormInstanceContent;
    if ('content' in tempValue && 'data' in tempValue.content) {
      submission = tempValue.content;
    } else {
      submission = { data: {}, isValid: true, changed: {} };
    }

    function setFormReadyWrapper(formioWebForm: FormioWebForm) {
      setIsFormReady(true);
      setInitialFormData(formioWebForm.data);
      setFormioWebForm(formioWebForm);
      resetErrors();
      setFormType('questionnaire');
    }

    const normalizedFormLanguage = toLanguageTag(selectedLanguage);

    return (
      <div
        className="questionnaire-container"
        data-testid="form"
        role="form"
        aria-label={
          previewI18nMap?.['view.taskkind.form'] ?? intl('view.taskkind.form')
        }
        aria-describedby={hasConditionalFields ? conditionalHintId : undefined}
      >
        {hasConditionalFields && (
          <>
            <p id={conditionalHintId} className="formio-conditional-hint">
              {previewI18nMap?.['questionnaire.conditional.hint'] ??
                intl('questionnaire.conditional.hint')}
            </p>
            <div
              className="formio-announcer"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {announcement}
            </div>
          </>
        )}

        <div className="custom-error-messages">
          {shouldShowErrors && Object.keys(errors).length > 0 ? (
            <CustomErrorMessage
              messages={Object.values(errors)
                .filter(Boolean)
                .map(e => ({
                  message: e.message,
                  id: e.component.id
                }))}
              previewI18nMap={previewI18nMap}
            />
          ) : null}
        </div>
        <Form
          key={`questionnaire-form-${normalizedFormLanguage}`}
          form={value.schema as FormIOSchema}
          submission={submission}
          options={options}
          onChange={onChangeWrapper}
          formReady={setFormReadyWrapper}
        />
      </div>
    );
  }, [
    announcement,
    conditionalHintId,
    errors,
    hasConditionalFields,
    onChangeWrapper,
    options,
    previewI18nMap,
    resetErrors,
    selectedLanguage,
    setFormType,
    setInitialFormData,
    shouldShowErrors,
    value
  ]);
},
questionnaireContainerPropsAreEqual);

/**
 * Update the FormInstance.
 * IMPORTANT!: Only alter the object value don't create a new one there is the risk of stale closure.
 *
 * @param value the FormInstance that has to be updated
 * @param record the changed data
 */
function updateFormInstance(
  value: FormInstance,
  record: FormInstanceContent
): FormInstance {
  console.group('Update questionnaireContainer', value.formType);
  console.dir(value);
  console.dir(record);
  console.groupEnd();

  //change from outside
  (value.content as Partial<typeof value.content>).changed = undefined;
  value.content.isValid = record.isValid;
  value.content.data = record.data;

  return value;
}

function CustomErrorMessage({
  messages,
  previewI18nMap
}: {
  readonly messages: { message: string; id: string }[];
  readonly previewI18nMap?: Record<string, string>;
}): React.JSX.Element {
  if (messages.length === 0) {
    return <></>;
  }

  return (
    <div className="error-inline-notification" role="alert">
      <div className="header-wrapper">
        <div>
          <Icon iconName="cancel" aria-label="" />
        </div>
        <p>
          {previewI18nMap?.['questionnaire.errors.fix'] ??
            intl('questionnaire.errors.fix')}
        </p>
      </div>
      <div className="default">
        <ul>
          {messages.map(({ message, id }) => (
            <li key={id}>{message}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function flattenComponentKeys(components: Component<any>[]): string[] {
  return components.reduce((acc: string[], c: Component<any>) => {
    acc.push(c.id);
    if (c.components) {
      acc.push(...flattenComponentKeys(c.components));
    }
    return acc;
  }, []);
}

export function getFormWithTextAndLanguage(isFieldRequired = false) {
  const commonValues = {
    classNames: '',
    placeholder: '',
    tableView: false,
    multiple: false,
    protected: false,
    unique: false,
    persistent: false,
    hidden: false,
    clearOnHide: false,
    modalEdit: false,
    dataGridLabel: false,
    labelPosition: 'top',
    hideLabel: false,
    disabled: false,
    autofocus: false,
    dbIndex: false,
    calculateServer: false,
    validateOn: 'change',
    validate: {
      required: isFieldRequired,
      customPrivate: false,
      strictDateValidation: false,
      multiple: false,
      unique: false,
      max: 2147483647,
      min: -2147483646
    },
    validators: [
      'required',
      'minLength',
      'maxLength',
      'minWords',
      'maxWords',
      'custom',
      'pattern',
      'json',
      'mask',
      'min',
      'max'
    ],
    allowCalculateOverride: false,
    encrypted: false,
    showCharCount: false,
    showWordCount: false,
    allowMultipleMasks: false
  };

  return {
    components: [
      //{
      //  selectOptions: [
      //    {
      //      selectLabel: 'I smoke every day at least 10 cigarettes',
      //      selectValue: 'one'
      //    },
      //    {
      //      selectLabel: 'I rarely make more than 4000 steps a day',
      //      selectValue: 'two'
      //    },
      //    {
      //      selectLabel: 'I eat fast food at least 3 times a week',
      //      selectValue: 'three'
      //    },
      //    {
      //      selectLabel: 'I drink alcohol at least 3 times a week',
      //      selectValue: 'four'
      //    },
      //    {
      //      selectLabel: 'I have a BMI over 25',
      //      selectValue: 'five'
      //    },
      //    {
      //      selectLabel: 'I have a BMI over 30',
      //      selectValue: 'six'
      //    },
      //    {
      //      selectLabel: 'None of the above',
      //      selectValue: 'seven'
      //    },
      //    {
      //      selectLabel: 'common.other',
      //      selectValue: ''
      //    }
      //  ],
      //  defaultValue: ['five', 'seven'],
      //  validateWhenHidden: false,
      //  validate: {
      //    custom:
      //      "valid = (input.selectLabel === 'common.other' && input.selectValue === '') ? false : true;",
      //    customMessage: 'fail!'
      //  },
      //  key: 'checkboxgroup-key-noDataGrid',
      //  type: 'ehsOpenChoiceCheckboxGroup',
      //  multiple: true,
      //  input: true,
      //  label: 'Radio smoke'
      //},
      {
        label: 'Emergency Contact',
        reorder: false,
        addAnotherPosition: 'bottom',
        layoutFixed: true,
        enableRowGroups: false,
        initEmpty: false,
        tableView: true,
        defaultValue: [
          {
            'text-key': 'test',
            'number-key': 123,
            'select-key':
              'virtualvisitoverviewportlet.identification.status.guest2',
            'time-key': '12:00',
            'datetime-key': 1732538198000,
            'date-key': 1732538198000,
            'multiselect-key': ['one', 'two'],
            'checkboxgroup-key': ['one', 'two'],
            'radio-key': 'one',
            'quantity-key': '80^^^kg'
          }
        ],
        key: 'emergencyContact2',
        type: 'ehsDataGrid',
        input: true,
        components: [
          {
            ...commonValues,
            key: 'text-key',
            label: 'Plain text label',
            input: true,
            type: 'ehsTextInput'
          },
          {
            ...commonValues,
            key: 'number-key',
            label: 'virtualvisitoverviewportlet.view.call.identificationnumber',
            type: 'ehsNumberInput'
          },
          {
            ...commonValues,
            key: 'quantity-key',
            label: 'Provide your weight and select correct unit.',
            input: true,
            type: 'ehsQuantity',
            decimalLimit: '4',
            validate: {
              required: true,
              pattern: '(^\\d+(\\.\\d{1,4})?\\^\\^\\^(kg|mg|dag)$)',
              customMessage: 'required'
            },
            unitOptions: [
              {
                unitLabel: 'Kilogram',
                unitValue: 'kg'
              },
              {
                unitLabel: 'Miligram',
                unitValue: 'mg'
              },
              {
                unitLabel: 'Dekagram',
                unitValue: 'dag'
              }
            ]
          },
          {
            ...commonValues,
            key: 'select-key',
            label: 'Select',
            defaultValue:
              'virtualvisitoverviewportlet.identification.status.guest',
            input: true,
            type: 'ehsSelect',
            selectOptions: [
              {
                selectLabel:
                  'virtualvisitpatientportlet.region.dropdown.placeholder',
                selectValue:
                  'virtualvisitoverviewportlet.identification.status.guest'
              },
              {
                selectLabel: 'common.error',
                selectValue:
                  'virtualvisitoverviewportlet.identification.status.guest2'
              },
              {
                selectLabel:
                  'virtualvisitoverviewportlet.identification.status.guest',
                selectValue:
                  'virtualvisitoverviewportlet.identification.status.guest3'
              }
            ]
          },
          {
            label: 'Multiple Select',
            ...commonValues,
            tableView: true,
            multiple: true,
            selectOptions: [
              {
                selectLabel: 'one',
                selectValue: 'one'
              },
              {
                selectLabel: 'two',
                selectValue: 'two'
              },
              {
                selectLabel: 'three',
                selectValue: 'three'
              }
            ],
            validateWhenHidden: false,
            key: 'multiselect-key',
            type: 'ehsSelect',
            input: true
          },
          //{
          //  ...commonValues,
          //  timepicker: true,
          //  key: 'datetime-key',
          //  label: 'This datetime',
          //  title: '',
          //  input: true,
          //  type: 'ehsdatetimequestionnairepicker'
          //},
          //{
          //  ...commonValues,
          //  timepicker: false,
          //  key: 'date-key',
          //  label: 'This date',
          //  title: '',
          //  input: true,
          //  validate: {
          //    required: false
          //  },
          //  type: 'ehsdatetimequestionnairepicker'
          //},
          //{
          //  ...commonValues,
          //  key: 'time-key',
          //  label: 'This Time ',
          //  input: true,
          //  type: 'ehstimepicker'
          //},

          {
            label: 'Select form all the options that apply',
            ...commonValues,
            tableView: true,

            selectOptions: [
              {
                selectLabel: 'I smoke every day at least 10 cigarettes',
                selectValue: 'one'
              },
              {
                selectLabel: 'I rarely make more than 4000 steps a day',
                selectValue: 'two'
              },
              {
                selectLabel: 'I eat fast food at least 3 times a week',
                selectValue: 'three'
              },
              {
                selectLabel: 'I drink alcohol at least 3 times a week',
                selectValue: 'four'
              },
              {
                selectLabel: 'I have a BMI over 25',
                selectValue: 'five'
              },
              {
                selectLabel: 'I have a BMI over 30',
                selectValue: 'six'
              },
              {
                selectLabel: 'None of the above',
                selectValue: 'seven'
              },
              {
                selectLabel: 'common.other',
                selectValue: ''
              }
            ],
            validateOn: 'blur',
            validate: {
              required: true,
              custom:
                "valid = (input.selectLabel === 'common.other' && input.selectValue === '') ? false : true;",
              customMessage: 'select'
            },
            validateWhenHidden: false,
            key: 'checkboxgroup-key',
            type: 'ehsOpenChoiceCheckboxGroup',
            multiple: true,
            input: true
          }
          //{
          //  ...commonValues,
          //  key: 'radio-key',
          //  label: 'Radio',
          //  input: true,
          //  type: 'ehsOpenChoiceRadio',
          //  classNames: '',
          //  radioOptions: [
          //    {
          //      radioLabel: 'This is first item',
          //      radioValue: 'one'
          //    },
          //    {
          //      radioLabel: 'This is second item',
          //      radioValue: 'two'
          //    },
          //    {
          //      radioLabel: 'This is default item',
          //      radioValue: 'three'
          //    },
          //    {
          //      radioLabel: 'This is fourth item',
          //      radioValue: 'four'
          //    },
          //    {
          //      radioLabel: '',
          //      radioValue: 'common.other'
          //    }
          //  ],
          //  validateOn: 'blur',
          //  validate: {
          //    required: true,
          //    custom:
          //      // for the other option we pass an object, the label cannot be empty
          //      "valid = (input && typeof input === 'object' && input.radioLabel === '') ? false : true;",
          //    customMessage: 'select'
          //  },
          //  validateWhenHidden: false
          //}
        ]
      }
    ]
  };
}
